import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import { Grid, Box, Paper, Popover } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useFormContext } from 'react-hook-form';
import { IDocument, ITask, NoteDTO, fileUploadToS3, getTaskById } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsEditor,
  WsFileUpload,
  WsSelect,
  WsTypoVariants,
  WsTypography,
  useWsEditor,
} from 'wisestep-base';

import WsTagsFilter from '../../../../components/filters/components/WsTagsFilter';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import useAdjustHeightOnZoom from '../../../../features/candidateLayout/hooks/useAdjustHeightOnZoom';
import { downloadCV } from '../../../../features/conversationWidget/utils/cvHelper';
import { uploadFileToS3 } from '../../../../features/importCandidates/components/uploadFromDevice/UploadFromDevice';
import { warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import { getDocumentFromNote, notesAmplitudeEvents } from './helper/notesHelper';
import NotesReminder from './NotesReminder';

interface IAddEditNote {
  onSubmit: SubmitHandler<FieldValues>;
  onCancel: () => void;
  note: NoteDTO;
  isAdding?: boolean;
}

const dropdownOptions = [
  { label: 'Phone', value: 'Phone' },
  { label: 'Email', value: 'Email' },
  { label: 'SMS', value: 'SMS' },
  { label: 'Other', value: 'Other' },
  { label: 'Personal Meeting', value: 'Personal Meeting' },
];

const AddEditNewNote = ({ onCancel, onSubmit, note, isAdding = false }: IAddEditNote) => {
  const methods = useFormContext();
  const { handleSubmit, getValues } = methods;
  const workspace = useWorkspaceContext();
  const maxFileSize = 3;
  const [openReminder, setOpenReminder] = useState(false);
  const [taskId, setTaskId] = useState<string>('');
  const [task, setTask] = useState<ITask>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const zoomLevel = useAdjustHeightOnZoom();
  const editor = useWsEditor(isAdding ? '' : note?.description ?? '', (htmlBody) =>
    methods.setValue('description', htmlBody ?? '')
  );
  const [otherDocument, setOtherDocument] = useState<IDocument[]>(
    getDocumentFromNote(note, isAdding)
  );
  const handleClose = () => setOpenReminder(false);

  useEffect(() => {
    const fetchTask = async () => {
      if (!isAdding) {
        const taskRes = await getTaskById(note.taskId ?? '');
        setTask(taskRes.data);
      }
    };
    fetchTask();
  }, []);

  const onUpload = async (files: File[]) => {
    notesAmplitudeEvents('note_attachment', 'Note Attachment', workspace);
    if (files.length == 0) {
      setNotification(warning('Upload your notes'));
      return;
    }
    const response = await uploadFileToS3(files);
    const alteredDocuments = [...otherDocument];
    for (let i = 0; i < response.bulkFileList.length; i++) {
      const file = response.bulkFileList[i];
      await fileUploadToS3(files[i], file.preSignedUrl ?? '');
      if (file.preSignedUrl) {
        alteredDocuments.push({
          documentLocation: file.preSignedUrl,
          originalName: file.fileName,
          tag: null,
          uploadedOn: new Date(),
        });
      }
    }
    setOtherDocument(alteredDocuments);
  };

  const removeDocHandler = (fileToRemove: string) => {
    setOtherDocument((prevDocuments) =>
      prevDocuments.filter((doc) => doc.documentLocation !== fileToRemove)
    );
  };

  const onSubmitHandler = (data: FieldValues) => {
    if (openReminder) return;
    const mandatoryFields = ['heading', 'noteType'];
    const emptyField = mandatoryFields.find((field) => isEmpty(data[field]));
    if (emptyField) {
      setNotification(warning('Please provide values for all mandatory fields'));
      return;
    }
    data.files = otherDocument.map(({ originalName, documentLocation }) => ({
      name: originalName,
      location: documentLocation,
    }));
    data.taskId = taskId;
    onSubmit(data);
  };

  const handleReminder = () => {
    if (isEmpty(getValues('heading'))) {
      setNotification(warning('Please fill the heading before creating reminder'));
      return;
    } else if (isEmpty(getValues('noteType'))) {
      setNotification(warning('Please select note type before creating reminder'));
      return;
    } else {
      note.heading = getValues('heading');
      note.description = getValues('description');
      setOpenReminder(true);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Paper
        sx={{
          borderColor: WsColors.TertiaryShades100,
          background: WsColors.White,
          maxHeight: `calc(${(1 + (1 - zoomLevel)) * 80}vh - 32px)`,
          overflowY: 'auto',
          minHeight: 590,
        }}
      >
        <Grid container direction="column" px={2} py={1} spacing={2} display="flex">
          <Grid container item spacing={2}>
            <Grid item xs={8} md={8}>
              <ControlledTextInput
                name="heading"
                fullWidth
                placeholder="Heading *"
                showMandatorySymbol={true}
                defaultValue={note?.heading}
              />
            </Grid>
            <Grid item xs={4} md={4}>
              <WsSelect
                name="noteType"
                selectArray={dropdownOptions}
                defaultValue={note?.noteType}
                size="small"
                placeholder="Note Type *"
                onChangeValue={() =>
                  notesAmplitudeEvents('note_type', 'Note Type Selection', workspace)
                }
              />
            </Grid>
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <WsTagsFilter placeHolder="Enter Tags" name="tags" label="" />
              </Grid>
              <Grid item xs={2} display="flex" justifyContent="flex-end">
                <WsButton
                  variant="outlined"
                  startIcon={<NotificationAddIcon />}
                  onClick={handleReminder}
                >
                  Add Reminder
                </WsButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={10}>
            <Box minHeight={210} border={1} borderColor={WsColors.BorderGrey}>
              <WsEditor key="description" editor={editor} resizable={true} />
            </Box>
          </Grid>
          <Grid container px={2} py={1} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Grid container alignItems="center">
                <Grid item>
                  {otherDocument?.map((document, index: number) => (
                    <Grid key={index} container direction="row" mb={2}>
                      <WsTypography variant={WsTypoVariants.BODY1}>
                        {document?.originalName ?? ''}
                      </WsTypography>
                      <DownloadIcon
                        onClick={() =>
                          downloadCV(
                            document?.documentLocation?.split('?')[0] ?? '',
                            document?.originalName ?? ''
                          )
                        }
                        sx={{
                          color: WsColors.TheBlues500,
                          fontSize: 24,
                          cursor: 'pointer',
                        }}
                      />
                      <CloseIcon
                        onClick={() => removeDocHandler(document?.documentLocation ?? '')}
                        sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                      />
                    </Grid>
                  ))}
                  <WsFileUpload
                    multiple={true}
                    onUpload={(file: File[]) => onUpload && onUpload(file)}
                    name="files"
                    placeholderText=""
                    bgRequired={false}
                    buttonText="Upload Documents"
                    maxFileSize={maxFileSize}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems="center">
                <WsButton variant="text" size="large" onClick={onCancel}>
                  Cancel
                </WsButton>
                <WsButton size="large" type="submit">
                  Save
                </WsButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Popover
          open={openReminder}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 850,
          }}
          PaperProps={{
            style: {
              width: 360,
              height: 500,
              borderRadius: 10,
            },
          }}
        >
          <NotesReminder
            updateReminder={!isAdding}
            closePopover={handleClose}
            note={note}
            setTaskId={setTaskId}
            task={task}
          />
        </Popover>
      </Paper>
    </form>
  );
};

export default AddEditNewNote;
